import {
	faBuilding,
	faSpinner,
	faSync,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ModalAtom from "../../../components/atoms/modal/modal";
import {
	useSyncAllMutation,
	useSyncProjectsMutation,
	useSyncUsersMutation,
} from "../../../store/services/capmoSync";
import { showToast } from "../../../utils/method";
import "./capmo-sync.scss";

// ──────────────────────────────────────────────────────────────────────────
// Stats display (used inside the results modal)
// ──────────────────────────────────────────────────────────────────────────
const STAT_BADGE_COLOR = {
	created: "success",
	updated: "primary",
	merged: "info",
	deleted: "danger",
	protectedFromDeletion: "warning",
	total: "dark",
};

const getBadgeColor = (statName, value) => {
	if (value === 0) return "secondary";
	return STAT_BADGE_COLOR[statName] || "primary";
};

const CATEGORY_ORDER = {
	projects: 1,
	ticketCategories: 2,
	projectMembers: 3,
	projectCompanies: 4,
	users: 5,
	companies: 6,
};

const SyncStatsContent = ({ stats, t }) => {
	if (!stats) return null;

	const getCategoryDisplayName = (category) =>
		t(`syncStats.categories.${category}`, { defaultValue: category });
	const getStatDisplayName = (statName) =>
		t(`syncStats.${statName}`, { defaultValue: statName });

	const sortedStats = Object.entries(stats).sort(
		([a], [b]) =>
			(CATEGORY_ORDER[a] ?? 999) - (CATEGORY_ORDER[b] ?? 999),
	);

	return (
		<Row className="gy-3">
			{sortedStats.map(([category, categoryStats]) => (
				<Col xs={12} key={category}>
					<Card className="mb-2 border bg-light">
						<Card.Header className="py-2 px-3 bg-light border-bottom">
							<h6 className="mb-0">
								{getCategoryDisplayName(category)}
							</h6>
						</Card.Header>
						<Card.Body className="py-2 px-3">
							<Row className="gy-2">
								{Object.entries(categoryStats).map(
									([statName, value]) => (
										<Col xs={6} md={4} key={statName}>
											<div className="d-flex align-items-center">
												<span className="text-muted me-2">
													{getStatDisplayName(statName)}:
												</span>
												<Badge
													bg={getBadgeColor(statName, value)}
												>
													{value}
												</Badge>
											</div>
										</Col>
									),
								)}
							</Row>
						</Card.Body>
					</Card>
				</Col>
			))}
		</Row>
	);
};

// ──────────────────────────────────────────────────────────────────────────
// Single sync option card
// ──────────────────────────────────────────────────────────────────────────
const SyncOptionCard = ({
	icon,
	tone,
	title,
	description,
	actionLabel,
	highlight,
	onAction,
	loading,
	disabled,
}) => (
	<Card className={`cs-action-card cs-action-card--${tone}${highlight ? " cs-action-card--highlight" : ""}`}>
		<div className="cs-action-card__icon">
			<FontAwesomeIcon icon={icon} />
		</div>
		<div className="cs-action-card__body">
			<h6 className="cs-action-card__title">{title}</h6>
			<p className="cs-action-card__description">{description}</p>
		</div>
		<div className="cs-action-card__footer">
			<Button
				variant={highlight ? "primary" : "outline-primary"}
				className="w-100 cs-action-card__btn"
				disabled={disabled || loading}
				onClick={onAction}
			>
				{loading ? (
					<>
						<FontAwesomeIcon icon={faSpinner} className="me-2 fa-spin" />
						<span>{actionLabel.loading}</span>
					</>
				) : (
					actionLabel.idle
				)}
			</Button>
		</div>
	</Card>
);

// ──────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────
function CapmoSyncPage() {
	const { t } = useTranslation();
	const [syncAllTrigger, { isLoading: loadingAll }] = useSyncAllMutation();
	const [syncProjectsTrigger, { isLoading: loadingProjects }] =
		useSyncProjectsMutation();
	const [syncUsersTrigger, { isLoading: loadingUsers }] =
		useSyncUsersMutation();

	const [showModal, setShowModal] = useState(false);
	const [syncType, setSyncType] = useState("");
	const [syncStats, setSyncStats] = useState(null);

	const anyLoading = loadingAll || loadingProjects || loadingUsers;

	const handleSync = async (type) => {
		try {
			const trigger =
				type === "projects"
					? syncProjectsTrigger
					: type === "users"
						? syncUsersTrigger
						: syncAllTrigger;
			const result = await trigger().unwrap();
			if (result?.message) {
				showToast(t(result.message), { type: "success" });
			}
			setSyncStats(result.stats);
			setSyncType(type);
			setShowModal(true);
		} catch (error) {
			showToast(
				error?.data?.message || error?.message || t("errors.general"),
				{ type: "error" },
			);
		}
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setSyncStats(null);
	};

	const modalTitle = useMemo(() => {
		const key =
			syncType === "projects"
				? "projects-sync-results"
				: syncType === "users"
					? "users-sync-results"
					: syncType === "all"
						? "all-sync-results"
						: "sync-results";
		return t(`navigation.capmo-import.${key}`);
	}, [syncType, t]);

	const loadingLabel = t("common.loading");

	return (
		<div className="page-container capmo-sync-page">
			<header className="cs-page-header">
				<h2 className="cs-page-header__title">
					{t("navigation.capmo-import.capmo-import")}
				</h2>
				<p className="cs-page-header__subtitle">
					{t("navigation.capmo-import.page-subtitle")}
				</p>
			</header>

			<Row className="cs-card-grid gy-4">
				<Col xs={12} md={6} lg={4}>
					<SyncOptionCard
						icon={faSync}
						tone="primary"
						highlight
						title={t("navigation.capmo-import.all")}
						description={t("navigation.capmo-import.all-description")}
						actionLabel={{
							idle: t("navigation.capmo-import.all"),
							loading: loadingLabel,
						}}
						onAction={() => handleSync("all")}
						loading={loadingAll}
						disabled={anyLoading}
					/>
				</Col>

				<Col xs={12} md={6} lg={4}>
					<SyncOptionCard
						icon={faBuilding}
						tone="info"
						title={t("navigation.capmo-import.projects")}
						description={t("navigation.capmo-import.projects-description")}
						actionLabel={{
							idle: t("navigation.capmo-import.projects"),
							loading: loadingLabel,
						}}
						onAction={() => handleSync("projects")}
						loading={loadingProjects}
						disabled={anyLoading}
					/>
				</Col>

				<Col xs={12} md={6} lg={4}>
					<SyncOptionCard
						icon={faUsers}
						tone="warn"
						title={t("navigation.capmo-import.users")}
						description={t("navigation.capmo-import.users-description")}
						actionLabel={{
							idle: t("navigation.capmo-import.users"),
							loading: loadingLabel,
						}}
						onAction={() => handleSync("users")}
						loading={loadingUsers}
						disabled={anyLoading}
					/>
				</Col>
			</Row>

			<ModalAtom
				show={showModal}
				handleClose={handleCloseModal}
				title={modalTitle}
				body={<SyncStatsContent stats={syncStats} t={t} />}
				saveText="common.close"
				handleSubmit={handleCloseModal}
				hideSaveButton={false}
				hideCloseButton={true}
			/>
		</div>
	);
}

export default CapmoSyncPage;
